'use client';

import { Fragment } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import BlogCardMD from './blogCardMD';
import CategoriesBottom from './CategoriesBottom';
import { Divider } from '@/components/global/utils/Divider';
import type { IArticle } from '@/lib/blog';
import Categories from './Categories';

type CategoriesArticlesGridClientProps = {
  activeCategory: string;
  categories: string[];
  articles: IArticle[];
};

const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: 'easeOut',
    },
  },
};

export default function CategoriesArticlesGridClient({
  activeCategory,
  categories,
  articles,
}: CategoriesArticlesGridClientProps) {
  return (
    <div id="categories" className="w-full ">
      <div className="w-full overflow-auto scrollbar-hide">
        <Categories
          elementId="categories"
          className="border-t-[.125rem]"
          categories={categories || []}
          activeCategory={activeCategory}
        />
      </div>

      <AnimatePresence mode="wait">
        {articles?.length === 0 ? (
          <span className="font-roboto text-center block py-[4rem] opacity-[0.45] font-medium">
            No articles found for this category.
          </span>
        ) : (
          <motion.ul
            key={activeCategory}
            className="lg:grid flex flex-col grid-cols-4 px-[2rem] lg:px-[5.5rem] pt-[6.63rem]"
            variants={containerVariants}
            initial="hidden"
            animate="show"
            exit={{
              opacity: 0,
              y: 10,
              transition: { duration: 0.2, ease: 'easeInOut' },
            }}
          >
            {articles?.map((article, index) => {
              const isLastColumn = (index + 1) % 4 === 0;
              const isHalf = index === 3; // divider central depois do 4º

              return (
                <Fragment key={article._id}>
                  <motion.li
                    className="flex lg:flex-row flex-col items-start"
                    variants={itemVariants}
                  >
                    <BlogCardMD article={article} />

                    {!isLastColumn && (
                      <Divider
                        orientation="vertical"
                        className="mx-[3rem] lg:block hidden  "
                      />
                    )}

                    <Divider
                      orientation="horizontal"
                      className=" lg:hidden block  my-[3rem]"
                    />
                  </motion.li>

                  {isHalf && (
                    <li className="col-span-4 lg:block hidden">
                      <Divider className="my-[4.5rem] bg-white/20" />
                    </li>
                  )}
                </Fragment>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
